import {
  DATE_TIME_FORMATS,
  IMAGE_NAME,
  ISSUE_STATUSES_KEYED,
} from "@miplatform/constants";
import { formatDate, getAssetUrl, getRootUrl } from "@miplatform/utils";
import { getAttemptTimeWindow } from "../util/emailFormAttemptUtil";
import { getEmailAttachmentSectionComponent } from "./EmailComponents/emailAttachmentSectionComponent";
import { getEmailDetailsContainer } from "./EmailComponents/emailDetailsComponent";
import { getEmailList } from "./EmailComponents/emailListComponent";
import { getEmailSectionContainer } from "./EmailComponents/emailSectionComponent";
import { getSectionItemComponent } from "./EmailComponents/emailSectionItemComponent";
import { getEmailTables } from "./EmailComponents/emailTableComponent";
import { getEmailTwoColumnSection } from "./EmailComponents/emailTwoColumnSectionComponent";
import EMAIL_TEMPLATE from "./emailTemplate";
import { ISSUE_STATUSES_COLOR_MAP } from "./emailTemplateConstants";

//  LOGO_URL
//  EMAIL_SUBJECT
//  EMAIL_DETAILS
//  TABLES
//  DETAILS
//  CONTACT_INFORMATION
//  ATTACHMENT_ICON
//  FOOTER_LOGO_URL
//  FOOTER_LOGO2_URL
//  YEAR

const formatField = (field) => field.trim().replaceAll(`\n`, `<br />`);

const processTemplate = (account) => {
  const API_URL = getRootUrl();

  return EMAIL_TEMPLATE.replace(
    "{{LOGO_URL}}",
    `${API_URL}/${account?.logo?.referenceId}`
  )
    .replace("{{FOOTER_LOGO_URL}}", getAssetUrl(IMAGE_NAME.MIVIEWIS))
    .replace("{{FOOTER_LOGO2_URL}}", getAssetUrl(IMAGE_NAME.MIBUILD))
    .replace("{{YEAR}}", new Date().getFullYear());
};

export const getAttemptEmailTemplate = ({
  entity: attempt,
  fields,
  attachments,
  account,
  config = {},
}) => {
  const accountOutlineIcon = `<image alt="icon" src="${getAssetUrl(
    IMAGE_NAME.ACCOUNT_OUTLINE
  )}" style="margin-top: 1.5px; width: 10px;height: 10px;"  />`;
  const arrivalWindow = getAttemptTimeWindow(attempt, fields.arrivalWindow);

  return processTemplate(account)
    .replace(
      "{{EMAIL_SUBJECT}}",
      `${attempt.assignment?.assignmentTemplateName} - #${
        attempt.assignment?.assignmentNumber
      } - ${formatDate(new Date(), DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY)}`
    )
    .replace("{{EMAIL_DETAILS}}", formatField(fields.details))
    .replace("{{CONTACT_INFORMATION}}", formatField(fields.contactInformation))
    .replace(
      "{{TABLES}}",
      getEmailTables([
        [
          {
            dataTestId: "email-address",
            label: "Address",
            value: `${attempt.home.address.addressLine1}, ${attempt.home.address.city}, ${attempt.home.address.regionCode} ${attempt.home.address.postalCode}`,
          },
          {
            dataTestId: "email-community",
            label: "Community",
            value: attempt.home.communityName || "-",
          },
          {
            dataTestId: "email-service-tech",
            label: "Service Tech",
            value: attempt.assignedUserName || "-",
          },
        ],
      ])
    )
    .replace(
      "{{DETAILS}}",
      config.showScheduledDate ||
        attempt.issues?.length > 0 ||
        fields.additionalNotes ||
        attachments?.length > 0
        ? getEmailDetailsContainer(
            `${
              config?.showScheduledDate
                ? getEmailSectionContainer({
                    icon: accountOutlineIcon,
                    title: "Scheduled Date and Arrival Window",
                    content: getEmailTwoColumnSection(
                      {
                        content: `Scheduled Date: ${formatDate(
                          attempt.scheduledStartDate,
                          DATE_TIME_FORMATS.FORMAT_MMMM_Do_YYYY
                        )}`,
                        dataTestId: "email-scheduled-date",
                      },
                      {
                        content: arrivalWindow,
                        dataTestId: "email-arrival-window",
                      }
                    ),
                  })
                : ""
            }
        ${
          attempt.issues?.length > 0
            ? getEmailSectionContainer({
                icon: accountOutlineIcon,
                title: "Order Tasks",
                marginTop: "8px",
                content: getEmailList(
                  attempt.issues.map(
                    ({ title, originalDescription, status, issueResult }) => {
                      const modifiedTitle = issueResult?.title || title;
                      const modifiedDescription =
                        issueResult?.originalDescription || originalDescription;
                      const modifiedStatus = issueResult?.status || status;
                      return {
                        title: modifiedTitle,
                        description: modifiedDescription,
                        ...(config?.showIssueStatus && {
                          chip: {
                            label:
                              ISSUE_STATUSES_KEYED[modifiedStatus]?.label ||
                              modifiedStatus,
                            colors:
                              ISSUE_STATUSES_COLOR_MAP[modifiedStatus] ||
                              ISSUE_STATUSES_COLOR_MAP.default,
                          },
                        }),
                      };
                    }
                  )
                ),
              })
            : ""
        }
        ${
          fields.additionalNotes
            ? getEmailSectionContainer({
                icon: accountOutlineIcon,
                title: "Additional Notes",
                marginTop: "8px",
                content: getSectionItemComponent({
                  isPadded: true,
                  content: formatField(fields.additionalNotes),
                }),
              })
            : ""
        }
        ${
          attachments?.length > 0
            ? getEmailSectionContainer({
                icon: `<image alt="icon" src="${getAssetUrl(
                  IMAGE_NAME.DOCUMENT_OUTLINE
                )}" style="width: 9px;height: 9px;"  />`,
                title: "View Linked Files",
                marginTop: "8px",
                content: getEmailAttachmentSectionComponent(attachments),
              })
            : ""
        }
        `
          )
        : ""
    )
    .replaceAll("{{ATTACHMENTS_ICON}}", getAssetUrl(IMAGE_NAME.FILE_CHECK));
};

export const getOrderEmailTemplate = ({
  entity: order,
  fields,
  attachments,
  account,
}) => {
  const accountOutlineIcon = `<image alt="icon" src="${getAssetUrl(
    IMAGE_NAME.ACCOUNT_OUTLINE
  )}" style="margin-top: 1.5px; width: 10px;height: 10px;"  />`;

  return processTemplate(account)
    .replace(
      "{{EMAIL_SUBJECT}}",
      `${order.orderTemplateName} Confirmation - ${formatDate(
        order.requestedStartDate,
        DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
      )}`
    )
    .replace("{{EMAIL_DETAILS}}", formatField(fields.details))
    .replace("{{CONTACT_INFORMATION}}", formatField(fields.contactInformation))
    .replace(
      "{{TABLES}}",
      getEmailTables([
        [
          {
            dataTestId: "email-address",
            label: "Address",
            value: `${order.home.address.addressLine1}, ${order.home.address.city}, ${order.home.address.regionCode} ${order.home.address.postalCode}`,
          },
          {
            dataTestId: "email-community",
            label: "Community",
            value: order.home.communityName || "-",
          },
          {
            dataTestId: "email-order",
            label: "Order",
            value: order.orderTemplateName || "-",
          },
        ],
      ])
    )
    .replace(
      "{{DETAILS}}",
      fields.additionalNotes || attachments?.length > 0
        ? getEmailDetailsContainer(
            `
        ${
          fields.additionalNotes
            ? getEmailSectionContainer({
                icon: accountOutlineIcon,
                title: "Additional Notes",
                marginTop: "8px",
                content: getSectionItemComponent({
                  isPadded: true,
                  content: formatField(fields.additionalNotes),
                }),
              })
            : ""
        }
        ${
          attachments?.length > 0
            ? getEmailSectionContainer({
                icon: `<image alt="icon" src="${getAssetUrl(
                  IMAGE_NAME.DOCUMENT_OUTLINE
                )}" style="width: 9px;height: 9px;"  />`,
                title: "View Linked Files",
                marginTop: "8px",
                content: getEmailAttachmentSectionComponent(attachments),
              })
            : ""
        }
        `
          )
        : ""
    )
    .replaceAll("{{ATTACHMENTS_ICON}}", getAssetUrl(IMAGE_NAME.FILE_CHECK));
};
